import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile, changeUserPassword, resetPassword, getIdToken } from '../services/firebase';
import { trackUserAction } from '../utils/analytics';
import {
  User,
  Lock,
  Mail,
  Shield,
  Globe,
  Search,
  ArrowRight,
  Check,
  X,
  LogOut,
  Sparkles,
  Volume2,
  PlayCircle,
} from 'lucide-react';
import { AgeBandPicker } from '../components/explore';
import { resetAgeBandPicker } from '../utils/ageBand';
import { usePremium, isPremiumMockEnabled } from '../utils/premiumStore';
import { useShowSearchInNavigation } from '../utils/navigationPreferencesStore';
import { useSearchFeatureEnabled } from '../utils/searchFeaturePreferencesStore';
import { useLessonNarrationAutoplay } from '../utils/lessonNarrationPreferencesStore';
import { getAutoplaysettings, saveAutoplaysettings, resetAutoplaysettings } from '../utils/autoplaysettings';
import LicenseStatusSection from '../components/settings/LicenseStatusSection';

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [premiumActive, setPremium, premiumState] = usePremium();
  const [showSearchInNav, setShowSearchInNav] = useShowSearchInNavigation();
  const [searchFeatureEnabled, setSearchFeatureEnabled] = useSearchFeatureEnabled();
  const [lessonNarrationAutoplay, setLessonNarrationAutoplay] = useLessonNarrationAutoplay();
  const [autoplay, setAutoplay] = useState(() => getAutoplaysettings());

  const updateAutoplay = (patch) => {
    setAutoplay(prev => {
      const next = { ...prev, ...patch };
      saveAutoplaysettings(next);
      return next;
    });
  };

  const stepperBtnStyle = {
    width: 28, height: 28, borderRadius: 6,
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg-card)',
    color: 'var(--color-text)',
    fontSize: 16, fontWeight: 700,
    cursor: 'pointer', lineHeight: 1, padding: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  };

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    displayName: user?.displayName || '',
    email: user?.email || ''
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await updateUserProfile(profileForm.displayName, profileForm.email);
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        trackUserAction.settingsUpdate('profile');
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    try {
      const result = await changeUserPassword(passwordForm.currentPassword, passwordForm.newPassword);
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        trackUserAction.settingsUpdate('password');
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await resetPassword(forgotEmail);
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Password reset email sent!' });
        setForgotEmail('');
        trackUserAction.settingsUpdate('forgot_password');
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send reset email' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await logout();
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Logged out successfully!' });
        trackUserAction.settingsUpdate('logout');
        // Redirect to home page after a brief delay
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      } else {
        setMessage({ type: 'error', text: result.message || 'Failed to logout' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to logout' });
    } finally {
      setLoading(false);
    }
  };

  // Get ID token for external links
  const [idToken, setIdToken] = useState('');

  React.useEffect(() => {
    const fetchIdToken = async () => {
      if (user) {
        const token = await getIdToken();
        setIdToken(token || '');
      }
    };
    fetchIdToken();
  }, [user]);

  const externalLinks = {
    webFiltering: `https://browser.kiddoware.com/login#idToken=${idToken}`,
    blockKeywords: `https://browser.kiddoware.com/login#idToken=${idToken}`,
    blockWebsites: `https://browser.kiddoware.com/login#idToken=${idToken}`,
  };

  const handleExternalLink = (linkType) => {
    trackUserAction.externalLink(linkType);
    window.open(externalLinks[linkType], '_blank');
  };

  return (
    <>
      <style>{`
        .settings-page {
          width: 100%;
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          padding-top: max(20px, env(safe-area-inset-top, 0px));
          padding-left: max(16px, env(safe-area-inset-left, 0px));
          padding-right: max(16px, env(safe-area-inset-right, 0px));
          min-height: 100vh;
          background: var(--color-bg);
          box-sizing: border-box;
          overflow-x: hidden;
        }
        .settings-card {
          background: var(--color-bg-card);
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 4px 24px var(--color-shadow);
          border: 1px solid var(--color-border);
          box-sizing: border-box;
          max-width: 100%;
          overflow: hidden;
        }
        .settings-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 24px;
          border-bottom: 1px solid var(--color-border);
          padding-bottom: 16px;
        }
        .settings-tab-btn {
          padding: 8px 14px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .settings-field-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          background: var(--color-bg);
          min-width: 0;
        }
        .settings-field-row input {
          flex: 1;
          min-width: 0;
          border: none;
          background: transparent;
          color: var(--color-text);
          font-size: 14px;
          outline: none;
        }
        @media (max-width: 768px) {
          .settings-page {
            padding: 12px;
            padding-top: max(68px, calc(12px + env(safe-area-inset-top, 0px)));
          }
          .settings-card {
            padding: 16px;
          }
          .settings-tab-btn {
            padding: 8px 12px;
            font-size: 13px;
          }
        }
      `}</style>
      <div className="settings-page">
      <div className="settings-card">
        <h1 style={{
          color: 'var(--color-text)',
          fontSize: 24,
          fontWeight: 'bold',
          marginBottom: 24,
          textAlign: 'center'
        }}>
          Settings
        </h1>

        {/* Message Display */}
        {message.text && (
          <div style={{
            padding: 12,
            borderRadius: 8,
            marginBottom: 20,
            background: message.type === 'success' ? '#dcfce7' : '#fef2f2',
            color: message.type === 'success' ? '#166534' : '#dc2626',
            border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            {message.type === 'success' ? <Check size={16} /> : <X size={16} />}
            {message.text}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="settings-tabs">
          <button
            onClick={() => setActiveTab('profile')}
            className="settings-tab-btn"
            style={{
              background: activeTab === 'profile' ? 'var(--color-primary)' : 'transparent',
              color: activeTab === 'profile' ? 'white' : 'var(--color-text)',
              fontWeight: activeTab === 'profile' ? 'bold' : 'normal'
            }}
          >
            Profile
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className="settings-tab-btn"
            style={{
              background: activeTab === 'password' ? 'var(--color-primary)' : 'transparent',
              color: activeTab === 'password' ? 'white' : 'var(--color-text)',
              fontWeight: activeTab === 'password' ? 'bold' : 'normal'
            }}
          >
            Password
          </button>
          <button
            onClick={() => setActiveTab('tools')}
            className="settings-tab-btn"
            style={{
              background: activeTab === 'tools' ? 'var(--color-primary)' : 'transparent',
              color: activeTab === 'tools' ? 'white' : 'var(--color-text)',
              fontWeight: activeTab === 'tools' ? 'bold' : 'normal'
            }}
          >
            Safety Tools
          </button>
          <button
            onClick={() => setActiveTab('account')}
            className="settings-tab-btn"
            style={{
              background: activeTab === 'account' ? 'var(--color-primary)' : 'transparent',
              color: activeTab === 'account' ? 'white' : 'var(--color-text)',
              fontWeight: activeTab === 'account' ? 'bold' : 'normal'
            }}
          >
            Account
          </button>
          <button
            onClick={() => setActiveTab('learner')}
            className="settings-tab-btn"
            style={{
              background: activeTab === 'learner' ? 'var(--color-primary)' : 'transparent',
              color: activeTab === 'learner' ? 'white' : 'var(--color-text)',
              fontWeight: activeTab === 'learner' ? 'bold' : 'normal',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Sparkles size={14} aria-hidden />
            Learner
          </button>
        </div>

        {activeTab === 'learner' && (
          <div>
            <h2 style={{ color: 'var(--color-text)', fontSize: 18, marginBottom: 16 }}>
              Learner preferences
            </h2>
            <AgeBandPicker compact />
            <div
              style={{
                marginTop: 22,
                padding: 16,
                borderRadius: 14,
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <Search size={18} aria-hidden style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                <h3
                  style={{
                    margin: 0,
                    fontSize: 15,
                    fontWeight: 800,
                    color: 'var(--color-text)',
                  }}
                >
                  Search on this device
                </h3>
              </div>
              <p
                style={{
                  margin: '0 0 12px',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'var(--color-text)',
                  opacity: 0.78,
                }}
              >
                Search is the only place a learner types their own words. Turning search <strong>off</strong> hides it
                everywhere on this browser (sidebar, Home link, and search pages) for a lessons-only session. Turning it{' '}
                <strong>on</strong> again restores search. An active <strong>Kiddoware</strong> or{' '}
                <strong>Kids Place Safe Browser</strong> license is also required — see the <strong>Account</strong> tab.
                Only someone who can open this signed-in Settings page can change device search.
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  marginBottom: 12,
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <span style={{ fontSize: 13, color: 'var(--color-text)', fontWeight: 600 }}>Current status</span>
                <span
                  aria-live="polite"
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    padding: '4px 12px',
                    borderRadius: 999,
                    background: searchFeatureEnabled
                      ? 'rgba(34,197,94,0.18)'
                      : 'rgba(148,163,184,0.22)',
                    color: 'var(--color-text)',
                  }}
                >
                  {searchFeatureEnabled ? 'Search is on' : 'Search is off'}
                </span>
              </div>
              <p
                style={{
                  margin: '0 0 12px',
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: 'var(--color-text)',
                  opacity: 0.72,
                }}
              >
                {searchFeatureEnabled
                  ? 'To disable search for this browser, use the button below.'
                  : 'To allow search again, use the button below.'}
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchFeatureEnabled(!searchFeatureEnabled);
                  trackUserAction.settingsUpdate('search_feature_enabled');
                }}
                style={{
                  width: '100%',
                  maxWidth: 420,
                  display: 'block',
                  background: searchFeatureEnabled ? '#b91c1c' : 'var(--color-primary)',
                  color: '#fff',
                  border: `1px solid ${searchFeatureEnabled ? '#b91c1c' : 'var(--color-primary)'}`,
                  borderRadius: 10,
                  padding: '12px 16px',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 2px 10px var(--color-shadow)',
                }}
              >
                {searchFeatureEnabled
                  ? 'Turn off search on this device'
                  : 'Turn search back on for this device'}
              </button>
            </div>
            <div
              style={{
                marginTop: 18,
                padding: 16,
                borderRadius: 14,
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg)',
                opacity: searchFeatureEnabled ? 1 : 0.55,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <Search size={18} aria-hidden style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                <h3
                  style={{
                    margin: 0,
                    fontSize: 15,
                    fontWeight: 800,
                    color: 'var(--color-text)',
                  }}
                >
                  Search in navigation
                </h3>
              </div>
              <p
                style={{
                  margin: '0 0 14px',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'var(--color-text)',
                  opacity: 0.78,
                }}
              >
                When this is on, <strong>Search</strong> appears in the <strong>left sidebar</strong> directly{' '}
                <strong>under Settings</strong> and above <strong>Lessons</strong> (magnifying glass icon). The web search
                link on Home is shown too. Turn off for a calmer, lessons-only surface while search stays available via
                URL.
              </p>
              <button
                type="button"
                disabled={!searchFeatureEnabled}
                onClick={() => {
                  setShowSearchInNav(!showSearchInNav);
                  trackUserAction.settingsUpdate('nav_search_visibility');
                }}
                style={{
                  background: showSearchInNav && searchFeatureEnabled ? 'var(--color-primary)' : 'transparent',
                  color: showSearchInNav && searchFeatureEnabled ? '#fff' : 'var(--color-text)',
                  border:
                    showSearchInNav && searchFeatureEnabled
                      ? '1px solid var(--color-primary)'
                      : '1px solid var(--color-border)',
                  borderRadius: 10,
                  padding: '10px 16px',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: searchFeatureEnabled ? 'pointer' : 'not-allowed',
                  boxShadow:
                    showSearchInNav && searchFeatureEnabled ? '0 2px 10px var(--color-shadow)' : 'none',
                }}
              >
                {!searchFeatureEnabled
                  ? 'Turn search back on above to change this'
                  : showSearchInNav
                    ? 'Search visible on Home & sidebar'
                    : 'Search hidden (lessons focus)'}
              </button>
            </div>
            <div
              style={{
                marginTop: 18,
                padding: 16,
                borderRadius: 14,
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <Volume2 size={18} aria-hidden style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                <h3
                  style={{
                    margin: 0,
                    fontSize: 15,
                    fontWeight: 800,
                    color: 'var(--color-text)',
                  }}
                >
                  Lesson narration autoplay
                </h3>
              </div>
              <p
                style={{
                  margin: '0 0 14px',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'var(--color-text)',
                  opacity: 0.78,
                }}
              >
                When this is on, guided lessons <strong>try to play</strong> the narration clip automatically when you
                open a step that has audio (intro, example, or recap). You can still pause or replay with the Listen
                button. Some browsers may block autoplay until you have tapped or clicked the page once. Turn off for a
                quieter, tap-to-listen experience.
              </p>
              <button
                type="button"
                onClick={() => {
                  setLessonNarrationAutoplay(!lessonNarrationAutoplay);
                  trackUserAction.settingsUpdate('lesson_narration_autoplay');
                }}
                style={{
                  background: lessonNarrationAutoplay ? 'var(--color-primary)' : 'transparent',
                  color: lessonNarrationAutoplay ? '#fff' : 'var(--color-text)',
                  border: lessonNarrationAutoplay
                    ? '1px solid var(--color-primary)'
                    : '1px solid var(--color-border)',
                  borderRadius: 10,
                  padding: '10px 16px',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: lessonNarrationAutoplay ? '0 2px 10px var(--color-shadow)' : 'none',
                }}
              >
                {lessonNarrationAutoplay
                  ? 'Autoplay on (play when step opens)'
                  : 'Autoplay off (tap Listen to play)'}
              </button>
            </div>
            {/* Story Autoplay */}
            <div
              style={{
                marginTop: 18,
                padding: 16,
                borderRadius: 14,
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg)',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <PlayCircle size={18} aria-hidden style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--color-text)' }}>
                  Story Autoplay
                </h3>
              </div>

              {/* Master toggle row */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }}>
                    Autoplay stories
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.72, lineHeight: 1.45, maxWidth: 340 }}>
                    Stories play automatically. Best used with audio on and adult nearby.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => updateAutoplay({ enabled: !autoplay.enabled })}
                  style={{
                    flexShrink: 0,
                    padding: '6px 14px',
                    borderRadius: 999,
                    border: autoplay.enabled ? '1px solid #16a34a' : '1px solid var(--color-border)',
                    background: autoplay.enabled ? '#16a34a' : 'transparent',
                    color: autoplay.enabled ? '#fff' : 'var(--color-text)',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {autoplay.enabled ? 'ON' : 'OFF'}
                </button>
              </div>

              {/* Rest of settings — greyed when disabled */}
              <div
                style={{
                  opacity: autoplay.enabled ? 1 : 0.4,
                  pointerEvents: autoplay.enabled ? 'auto' : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 18,
                  borderTop: '1px solid var(--color-border)',
                  paddingTop: 16,
                }}
              >
                {/* Scene timing */}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)', marginBottom: 8 }}>
                    Scene timing
                  </div>
                  {[
                    { value: 'audio', label: 'Follow audio' },
                    { value: 'text', label: 'Match text length' },
                    { value: 'fixed', label: 'Fixed time' },
                  ].map(({ value, label }) => (
                    <label
                      key={value}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer', fontSize: 13, color: 'var(--color-text)' }}
                    >
                      <input
                        type="radio"
                        name="autoplay-timingMode"
                        value={value}
                        checked={autoplay.timingMode === value}
                        onChange={() => updateAutoplay({ timingMode: value })}
                      />
                      {label}
                      {value === 'fixed' && autoplay.timingMode === 'fixed' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
                          <button type="button" style={stepperBtnStyle} onClick={() => updateAutoplay({ fixedSeconds: Math.max(1, autoplay.fixedSeconds - 1) })}>−</button>
                          <span style={{ minWidth: 24, textAlign: 'center', fontSize: 13, fontWeight: 700 }}>{autoplay.fixedSeconds}</span>
                          <button type="button" style={stepperBtnStyle} onClick={() => updateAutoplay({ fixedSeconds: Math.min(10, autoplay.fixedSeconds + 1) })}>+</button>
                          <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.7 }}>sec</span>
                        </span>
                      )}
                    </label>
                  ))}
                </div>

                {/* After each story */}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)', marginBottom: 8 }}>
                    After each story
                  </div>
                  {[
                    { value: true, label: 'Start next story automatically' },
                    { value: false, label: 'Wait for child to choose' },
                  ].map(({ value, label }) => (
                    <label
                      key={String(value)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer', fontSize: 13, color: 'var(--color-text)' }}
                    >
                      <input
                        type="radio"
                        name="autoplay-autoNext"
                        checked={autoplay.autoNext === value}
                        onChange={() => updateAutoplay({ autoNext: value })}
                      />
                      {label}
                    </label>
                  ))}
                </div>

                {/* Learning exercises toggle */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }}>
                        Require exercise between stories
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.72, lineHeight: 1.45, maxWidth: 340 }}>
                        When off, the learning activity is shown briefly then skipped.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateAutoplay({ requireExercise: !autoplay.requireExercise })}
                      style={{
                        flexShrink: 0,
                        padding: '6px 14px',
                        borderRadius: 999,
                        border: autoplay.requireExercise ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                        background: autoplay.requireExercise ? 'var(--color-primary)' : 'transparent',
                        color: autoplay.requireExercise ? '#fff' : 'var(--color-text)',
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      {autoplay.requireExercise ? 'ON' : 'OFF'}
                    </button>
                  </div>

                  {/* Exercise timeout — only when requireExercise is OFF */}
                  {!autoplay.requireExercise && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: 'var(--color-text)' }}>Show exercise for</span>
                        <button type="button" style={stepperBtnStyle} onClick={() => updateAutoplay({ exerciseTimeoutSeconds: Math.max(5, autoplay.exerciseTimeoutSeconds - 1) })}>−</button>
                        <span style={{ minWidth: 24, textAlign: 'center', fontSize: 13, fontWeight: 700 }}>{autoplay.exerciseTimeoutSeconds}</span>
                        <button type="button" style={stepperBtnStyle} onClick={() => updateAutoplay({ exerciseTimeoutSeconds: Math.min(15, autoplay.exerciseTimeoutSeconds + 1) })}>+</button>
                        <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.7 }}>sec</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.65, lineHeight: 1.4 }}>
                        Then automatically continues to next story
                      </div>
                    </div>
                  )}
                </div>

                {/* Stop after */}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)', marginBottom: 8 }}>
                    Stop after
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer', fontSize: 13, color: 'var(--color-text)' }}>
                    <input
                      type="radio"
                      name="autoplay-stopMode"
                      checked={autoplay.stopMode === 'forever'}
                      onChange={() => updateAutoplay({ stopMode: 'forever' })}
                    />
                    Play forever
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer', fontSize: 13, color: 'var(--color-text)', flexWrap: 'wrap' }}>
                    <input
                      type="radio"
                      name="autoplay-stopMode"
                      checked={autoplay.stopMode === 'stories'}
                      onChange={() => updateAutoplay({ stopMode: 'stories' })}
                    />
                    Stop after
                    <button type="button" style={stepperBtnStyle} onClick={() => updateAutoplay({ stopAfterStories: Math.max(1, autoplay.stopAfterStories - 1) })}>−</button>
                    <span style={{ minWidth: 24, textAlign: 'center', fontSize: 13, fontWeight: 700 }}>{autoplay.stopAfterStories}</span>
                    <button type="button" style={stepperBtnStyle} onClick={() => updateAutoplay({ stopAfterStories: Math.min(20, autoplay.stopAfterStories + 1) })}>+</button>
                    <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.7 }}>stories</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0, cursor: 'pointer', fontSize: 13, color: 'var(--color-text)', flexWrap: 'wrap' }}>
                    <input
                      type="radio"
                      name="autoplay-stopMode"
                      checked={autoplay.stopMode === 'minutes'}
                      onChange={() => updateAutoplay({ stopMode: 'minutes' })}
                    />
                    Stop after
                    <button type="button" style={stepperBtnStyle} onClick={() => updateAutoplay({ stopAfterMinutes: Math.max(10, autoplay.stopAfterMinutes - 1) })}>−</button>
                    <span style={{ minWidth: 28, textAlign: 'center', fontSize: 13, fontWeight: 700 }}>{autoplay.stopAfterMinutes}</span>
                    <button type="button" style={stepperBtnStyle} onClick={() => updateAutoplay({ stopAfterMinutes: Math.min(60, autoplay.stopAfterMinutes + 1) })}>+</button>
                    <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.7 }}>min</span>
                  </label>
                </div>

                {/* Pause between stories */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)' }}>Pause between stories</span>
                    <button type="button" style={stepperBtnStyle} onClick={() => updateAutoplay({ pauseBetweenSeconds: Math.max(1, autoplay.pauseBetweenSeconds - 1) })}>−</button>
                    <span style={{ minWidth: 24, textAlign: 'center', fontSize: 13, fontWeight: 700 }}>{autoplay.pauseBetweenSeconds}</span>
                    <button type="button" style={stepperBtnStyle} onClick={() => updateAutoplay({ pauseBetweenSeconds: Math.min(10, autoplay.pauseBetweenSeconds + 1) })}>+</button>
                    <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.7 }}>sec</span>
                  </div>
                </div>
              </div>

              {/* Reset to defaults */}
              <div style={{ marginTop: 16, borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
                <button
                  type="button"
                  onClick={() => setAutoplay(resetAutoplaysettings())}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-primary)',
                    fontSize: 13,
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  Reset to defaults
                </button>
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <button
                type="button"
                onClick={() => resetAgeBandPicker()}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 10,
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: 'var(--color-text)',
                }}
              >
                Show the age picker again on Home
              </button>
            </div>

            {isPremiumMockEnabled() ? (
              <div
                style={{
                  marginTop: 28,
                  padding: 16,
                  borderRadius: 14,
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-bg)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 15,
                      fontWeight: 800,
                      color: 'var(--color-text)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Sparkles size={16} aria-hidden />
                    Premium (test mode)
                  </h3>
                  <span
                    aria-live="polite"
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: premiumActive
                        ? 'rgba(34,197,94,0.18)'
                        : 'rgba(148,163,184,0.18)',
                      color: 'var(--color-text)',
                    }}
                  >
                    {premiumActive ? 'ON' : 'OFF'}
                  </span>
                </div>
                <p
                  style={{
                    margin: '0 0 12px',
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: 'var(--color-text)',
                    opacity: 0.75,
                  }}
                >
                  {premiumActive ? (
                    <>
                      Every gated learning path is unlocked on this device for testing.
                      {premiumState?.since
                        ? ` Active since ${new Date(premiumState.since).toLocaleString()}.`
                        : ''}{' '}
                      No real billing has happened.
                    </>
                  ) : (
                    <>
                      Free tier is active — only the first path in each world is open. Toggle this
                      on to preview the full premium experience without entering payment details.
                    </>
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => setPremium(!premiumActive)}
                  style={{
                    background: premiumActive ? 'transparent' : 'var(--color-primary)',
                    color: premiumActive ? 'var(--color-text)' : '#fff',
                    border: premiumActive
                      ? '1px solid var(--color-border)'
                      : '1px solid var(--color-primary)',
                    borderRadius: 10,
                    padding: '10px 16px',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: premiumActive ? 'none' : '0 2px 10px var(--color-shadow)',
                  }}
                >
                  {premiumActive ? 'Turn off premium (test mode)' : 'Turn on premium (test mode)'}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div>
            <h2 style={{ color: 'var(--color-text)', fontSize: 18, marginBottom: 16 }}>
              Update Profile
            </h2>
            <form onSubmit={handleProfileUpdate}>
              <div style={{ marginBottom: 16 }}>
                <label style={{
                  display: 'block',
                  color: 'var(--color-text)',
                  fontSize: 14,
                  fontWeight: 'bold',
                  marginBottom: 8
                }}>
                  Name
                </label>
                <div className="settings-field-row">
                  <User size={20} color="var(--color-secondary)" style={{ flexShrink: 0 }} />
                  <input
                    type="text"
                    value={profileForm.displayName}
                    onChange={(e) => setProfileForm({...profileForm, displayName: e.target.value})}
                    placeholder="Enter your name"
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{
                  display: 'block',
                  color: 'var(--color-text)',
                  fontSize: 14,
                  fontWeight: 'bold',
                  marginBottom: 8
                }}>
                  Email Address
                </label>
                <div className="settings-field-row">
                  <Mail size={20} color="var(--color-secondary)" style={{ flexShrink: 0 }} />
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                    placeholder="Enter email address"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 'bold',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
            </form>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div>
            <h2 style={{ color: 'var(--color-text)', fontSize: 18, marginBottom: 16 }}>
              Change Password
            </h2>
            
            {/* Change Password Form */}
            <form onSubmit={handlePasswordChange} style={{ marginBottom: 32 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{
                  display: 'block',
                  color: 'var(--color-text)',
                  fontSize: 14,
                  fontWeight: 'bold',
                  marginBottom: 8
                }}>
                  Current Password
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  background: 'var(--color-bg)'
                }}>
                  <Lock size={20} color="var(--color-secondary)" />
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                    placeholder="Enter current password"
                    style={{
                      flex: 1,
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--color-text)',
                      fontSize: 14,
                      outline: 'none'
                    }}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{
                  display: 'block',
                  color: 'var(--color-text)',
                  fontSize: 14,
                  fontWeight: 'bold',
                  marginBottom: 8
                }}>
                  New Password
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  background: 'var(--color-bg)'
                }}>
                  <Lock size={20} color="var(--color-secondary)" />
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    placeholder="Enter new password"
                    style={{
                      flex: 1,
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--color-text)',
                      fontSize: 14,
                      outline: 'none'
                    }}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{
                  display: 'block',
                  color: 'var(--color-text)',
                  fontSize: 14,
                  fontWeight: 'bold',
                  marginBottom: 8
                }}>
                  Confirm New Password
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  background: 'var(--color-bg)'
                }}>
                  <Lock size={20} color="var(--color-secondary)" />
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    placeholder="Confirm new password"
                    style={{
                      flex: 1,
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--color-text)',
                      fontSize: 14,
                      outline: 'none'
                    }}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 'bold',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </form>

            {/* Forgot Password Section */}
            <div style={{
              borderTop: '1px solid var(--color-border)',
              paddingTop: 24
            }}>
              <h3 style={{ color: 'var(--color-text)', fontSize: 16, marginBottom: 16 }}>
                Forgot Password?
              </h3>
              <form onSubmit={handleForgotPassword}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{
                    display: 'block',
                    color: 'var(--color-text)',
                    fontSize: 14,
                    fontWeight: 'bold',
                    marginBottom: 8
                  }}>
                    Email Address
                  </label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    background: 'var(--color-bg)'
                  }}>
                    <Mail size={20} color="var(--color-secondary)" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Enter your email"
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--color-text)',
                        fontSize: 14,
                        outline: 'none'
                      }}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    background: 'var(--color-secondary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 'bold',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? 'Sending...' : 'Send Reset Email'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Safety Tools Tab */}
        {activeTab === 'tools' && (
          <div>
            <h2 style={{ color: 'var(--color-text)', fontSize: 18, marginBottom: 16 }}>
              Safety Tools & Parental Controls
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                onClick={() => handleExternalLink('webFiltering')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = 'var(--color-primary)'}
                onMouseOut={(e) => e.target.style.background = 'var(--color-bg)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Globe size={24} color="var(--color-primary)" />
                  <div>
                    <div style={{ color: 'var(--color-text)', fontWeight: 'bold', fontSize: 14 }}>
                      Manage Web Filtering Content
                    </div>
                    <div style={{ color: 'var(--color-secondary)', fontSize: 12 }}>
                      Control what content your children can access
                    </div>
                  </div>
                </div>
                <ArrowRight size={20} color="var(--color-secondary)" />
              </button>

              <button
                onClick={() => handleExternalLink('blockKeywords')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = 'var(--color-primary)'}
                onMouseOut={(e) => e.target.style.background = 'var(--color-bg)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Shield size={24} color="var(--color-primary)" />
                  <div>
                    <div style={{ color: 'var(--color-text)', fontWeight: 'bold', fontSize: 14 }}>
                      Block Keywords
                    </div>
                    <div style={{ color: 'var(--color-secondary)', fontSize: 12 }}>
                      Block specific keywords and phrases
                    </div>
                  </div>
                </div>
                <ArrowRight size={20} color="var(--color-secondary)" />
              </button>

              <button
                onClick={() => handleExternalLink('blockWebsites')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = 'var(--color-primary)'}
                onMouseOut={(e) => e.target.style.background = 'var(--color-bg)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Shield size={24} color="var(--color-primary)" />
                  <div>
                    <div style={{ color: 'var(--color-text)', fontWeight: 'bold', fontSize: 14 }}>
                      Block Websites
                    </div>
                    <div style={{ color: 'var(--color-secondary)', fontSize: 12 }}>
                      Block specific websites and domains
                    </div>
                  </div>
                </div>
                <ArrowRight size={20} color="var(--color-secondary)" />
              </button>
            </div>
          </div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div>
            <h2 style={{ color: 'var(--color-text)', fontSize: 18, marginBottom: 16 }}>
              Account Management
            </h2>

            <LicenseStatusSection />

            <div style={{
              padding: '20px',
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              marginBottom: 20
            }}>
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ color: 'var(--color-text)', fontSize: 16, marginBottom: 8 }}>
                  Current User
                </h3>
                <div style={{ color: 'var(--color-secondary)', fontSize: 14 }}>
                  {user?.displayName || user?.email}
                </div>
              </div>
              
              <div style={{
                borderTop: '1px solid var(--color-border)',
                paddingTop: 16
              }}>
                <h3 style={{ color: 'var(--color-text)', fontSize: 16, marginBottom: 12 }}>
                  Sign Out
                </h3>
                <p style={{ color: 'var(--color-secondary)', fontSize: 14, marginBottom: 16 }}>
                  Sign out of your account. You'll need to sign in again to access your settings.
                </p>
                <button
                  onClick={handleLogout}
                  disabled={loading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '12px 24px',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 'bold',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => !loading && (e.target.style.background = '#b91c1c')}
                  onMouseOut={(e) => !loading && (e.target.style.background = '#dc2626')}
                >
                  <LogOut size={16} />
                  {loading ? 'Signing Out...' : 'Sign Out'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default SettingsPage; 